import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit, signal } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';
import { SelectionModelService } from '@project/components/article/service/selection-model.service';
import { BmkService } from '@project/service/bmk.service';
import { AlertHelper } from '@shared/alert/alert.helper';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { BmkStructure, isBmkStructure } from './bmk.structure';

@Component({
  selector: 'app-edit-bmk',
  templateUrl: './edit-bmk.component.html',
  imports: [CommonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditBmkComponent implements OnInit {

  private _bmkStructure = signal<BmkStructure | string>(new BmkStructure());
  public bmkStructure = this._bmkStructure.asReadonly();
  public existingBmkNames: string[] = [];

  constructor(
    private bsModalRef: BsModalRef,
    private bmkService: BmkService,
    private selectionModelService: SelectionModelService,
    private alertHelper: AlertHelper,
  ) {
  }

  public ngOnInit(): void {
    if(!this.bmkService.isEditBmkGroupAvailableForArticles(this.selectionModelService.getArticleContainers())) {
      this.alertHelper.showError("editBmk.error.title", "editBmk.error.articles_not_neighbors.description")
        .then(() => this.close());
      }
    const selectedArticles = this.selectionModelService.getArticleContainers();
    this.existingBmkNames = this.bmkService.readBmkNamesFromArticles(selectedArticles).filter((bmkName) => !!bmkName);
    if (this.existingBmkNames.length === 1) {
      const parsed = BmkStructure.parseBmkString(this.existingBmkNames[0]);
      this._bmkStructure.set(parsed);
    } else if (this.existingBmkNames.length > 1) {
      const combined = BmkStructure.getCommonPartsFromBmkStrings(this.existingBmkNames);
      this._bmkStructure.set(combined);
    }
  }

  public submit(bmkName: string): void {
    const selectedArticles = this.selectionModelService.getArticleContainers();
    this.bmkService.setBmkGroup(selectedArticles, bmkName);
    this.close();
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public bmkStringChanged(bmkString: string): void {
    this._bmkStructure.set(BmkStructure.parseBmkString(bmkString));
  }

  functionalAssignmentChanged(event: Event) {
    const newFunctionalAssignment = this.removeInvalidCharacters(event.target as HTMLInputElement);
    const currentBmk = this._bmkStructure();
    if (isBmkStructure(currentBmk)) {
      this._bmkStructure.set(currentBmk.with({functionalAssignment: currentBmk.functionalAssignment.setText(newFunctionalAssignment)}));
    }
  }

  functionDesignationChanged(event: Event) {
    const newFunctionDesignation = this.removeInvalidCharacters(event.target as HTMLInputElement);
    const currentBmk = this._bmkStructure();
    if (isBmkStructure(currentBmk)) {
      this._bmkStructure.set(currentBmk.with({functionDesignation: currentBmk.functionDesignation.setText(newFunctionDesignation)}));
    }
  }

  installationSiteChanged(event: Event) {
    const newInstallationSite = this.removeInvalidCharacters(event.target as HTMLInputElement);
    const currentBmk = this._bmkStructure();
    if (isBmkStructure(currentBmk)) {
      this._bmkStructure.set(currentBmk.with({installationSite: currentBmk.installationSite.setText(newInstallationSite)}));
    }
  }

  locationDesignationChanged(event: Event) {
    const newLocationDesignation = this.removeInvalidCharacters(event.target as HTMLInputElement);
    const currentBmk = this._bmkStructure();
    if (isBmkStructure(currentBmk)) {
      this._bmkStructure.set(currentBmk.with({locationDesignation: currentBmk.locationDesignation.setText(newLocationDesignation)}));
    }
  }

  deviceTagChanged(event: Event) {
    const newDeviceTag = this.removeInvalidCharacters(event.target as HTMLInputElement);
    const currentBmk = this._bmkStructure();
    if (isBmkStructure(currentBmk)) {
      this._bmkStructure.set(currentBmk.with({deviceTag: currentBmk.deviceTag.setText(newDeviceTag)}));
    }
  }

  private removeInvalidCharacters(htmlInputElement: HTMLInputElement): string {
    htmlInputElement.value = htmlInputElement.value.replace(/[≠=+\-]/g, '');
    return htmlInputElement.value;
  }

  protected readonly isBmkStructure = isBmkStructure;
}
